import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { fetchMandiListings } from './data';
import { slugify } from '../market-intelligence/utils';
import { buildMarketplaceBuyUrl } from '../live-tender-info/matching';
import Footer from '../home/components/Footer';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';
import { useAuth } from '../../contexts/AuthContext';

const NAVY = '#0F2238';
const AMBER = '#F59E0B';
const AMBER_DEEP = '#B45309';
const CREAM = '#FDF8F0';
const BORDER = '#E7E0D3';
const MUTED = '#78716C';
const FAINT = '#A8A29E';

const GlobalStyle = () => (
  <style>{`
    @keyframes cardIn { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    @keyframes glowPulse { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,0.45); } 70% { box-shadow: 0 0 0 8px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
    @keyframes chipIn { from { opacity: 0; transform: scale(.85); } to { opacity: 1; transform: scale(1); } }
    .card-enter { animation: cardIn .38s cubic-bezier(.22,1,.36,1) both; }
    .district-unlock { animation: glowPulse .7s ease-out; }
    .chip-enter { animation: chipIn .18s ease-out both; }
    .expand-wrap { display: grid; transition: grid-template-rows .32s cubic-bezier(.22,1,.36,1); }
    .expand-inner { overflow: hidden; }
    select:disabled { cursor: not-allowed; }
  `}</style>
);

const BuySugarButton = ({ label, full, disabled, onClick }) => {
  const { t } = useTranslation();
  return (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center justify-center gap-1.5 font-bold text-white rounded-xl transition-all duration-150 hover:opacity-90 hover:shadow-md active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed ${full ? 'w-full py-3 text-sm' : 'px-3.5 py-2 text-xs'}`}
    style={{ background: disabled ? '#A8A29E' : '#ff8c00', boxShadow: disabled ? 'none' : '0 2px 8px rgba(255,140,0,0.35)' }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#e67e00'; }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = '#ff8c00'; }}
  >
    <svg width={full ? 15 : 12} height={full ? 15 : 12} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.6 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6" /></svg>
    {label || t('marketplace.buySugar', 'Buy Sugar')}
  </button>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const FactoryCard = ({ f, index }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [toast, setToast] = useState('');
  const navigate = useNavigate();
  const fireToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 1800); };
  const detailPath = `/mandi/${slugify(f.name)}`;

  // Buy Sugar here is a Mandi -> Marketplace continuation, not a purchase itself:
  // an authenticated user goes straight to the matching offer in the Buyer
  // Marketplace; an unauthenticated one goes through the existing login gate
  // (?next=) and lands on the same destination afterwards. See
  // buildMarketplaceBuyUrl() for why factory name + grade (not this quote's id)
  // is what the Marketplace resolves against.
  const handleBuySugar = (grade, mandiQuoteId) => {
    const destination = buildMarketplaceBuyUrl({ factoryName: f.name, grade, mandiQuoteId });
    if (isAuthenticated) {
      navigate(destination);
      return;
    }
    fireToast(t('mandi.signInToBuy', 'Sign in to buy {{grade}} from {{factory}}', { grade, factory: f.name }));
    setTimeout(() => navigate(`/login-authentication?next=${encodeURIComponent(destination)}`), 500);
  };

  const handleShare = (e) => {
    e.preventDefault();
    const url = `${window.location.origin}${detailPath}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      fireToast(t('mandi.linkCopied', 'Link copied'));
    }
  };

  return (
    <div
      className="card-enter rounded-2xl overflow-hidden self-start"
      style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, boxShadow: '0 2px 6px rgba(15,34,56,0.06)', animationDelay: `${Math.min(index, 8) * 45}ms` }}
    >
      <div className="px-4 pt-4 pb-2.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0" style={{ background: '#FFF3E0', color: AMBER_DEEP, border: '1px solid #FDE1B8' }}>{f.millCode}</span>
            <span className="text-[10px] truncate" style={{ color: FAINT }}>{f.district}, {f.state}</span>
          </div>
          <button onClick={handleShare} className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: CREAM }}>
            <Icon name="Share2" size={12} color={AMBER_DEEP} />
          </button>
        </div>
        <Link to={detailPath} className="block">
          <h3 className="text-sm font-black leading-snug hover:underline" style={{ color: NAVY }}>{f.name}</h3>
        </Link>
        {f.quotaQty != null && (
          <p className="text-[10px] mt-0.5" style={{ color: AMBER_DEEP }}>
            {f.quotaMonthLabel} {t(f.quotaIsFortnight ? 'mandi.fortnightQuotaLabel' : 'mandi.quotaLabel', f.quotaIsFortnight ? 'Fortnight Quota' : 'Quota')}: <span className="font-bold">{f.quotaQty.toLocaleString('en-IN')} MT</span>
          </p>
        )}
      </div>

      {/* Every grade shown flat — full rate/date/qty detail always visible, no click needed */}
      <div style={{ borderTop: `1px solid ${BORDER}` }}>
        {f.grades.filter((g) => g.availableQty > 0).map((g, i) => {
          const todayStr = new Date()?.toISOString()?.slice(0, 10);
          const expired = g.liftingDate && g.liftingDate < todayStr;
          const disabled = expired;
          return (
            <div key={g.id} className="px-4 py-3" style={{ borderTop: i > 0 ? `1px solid ${BORDER}` : 'none' }}>
              <div className="flex items-center justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-black" style={{ background: '#FEF3C7', color: AMBER_DEEP }}>
                    {g.grade.split('-')[0]}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-black leading-none" style={{ color: NAVY }}>&#8377;{g.rateExclGst.toLocaleString('en-IN')}<span className="text-[10px] font-semibold" style={{ color: MUTED }}>/MT</span></p>
                    <p className="text-[10px] font-bold mt-1" style={{ color: disabled ? FAINT : '#DC2626' }}>{expired ? t('mandi.offerExpired', 'Offer Expired') : t('mandi.mtAvailable', '{{qty}} MT available', { qty: g.availableQty })}</p>
                  </div>
                </div>
                <BuySugarButton disabled={disabled} onClick={() => handleBuySugar(g.grade, g.id)} />
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[11px] rounded-lg p-2.5" style={{ color: MUTED, background: CREAM }}>
                <div>{t('mandi.rateExclGst', 'Rate Excl GST')}<br /><span className="font-semibold" style={{ color: NAVY }}>&#8377;{g.rateExclGst.toLocaleString('en-IN')}/MT</span></div>
                <div>{t('mandi.rateInclGst', 'Rate Incl GST')}<br /><span className="font-semibold" style={{ color: NAVY }}>&#8377;{g.rateInclGst.toLocaleString('en-IN')}/MT</span></div>
                <div>{t('marketplace.paymentDate', 'Payment Date')}<br /><span className="font-semibold" style={{ color: NAVY }}>{fmtDate(g.paymentDate)}</span></div>
                <div>{t('marketplace.liftingDate', 'Lifting Date')}<br /><span className="font-semibold" style={{ color: NAVY }}>{fmtDate(g.liftingDate)}</span></div>
              </div>
            </div>
          );
        })}
      </div>

      <Link to={detailPath} className="block px-4 py-2.5 text-center text-xs font-bold" style={{ borderTop: `1px solid ${BORDER}`, color: AMBER_DEEP, background: CREAM }}>
        {t('mandi.viewFullDetails', 'View full factory details')} →
      </Link>

      {toast && <div className="px-4 py-2 text-[10px] font-semibold text-center" style={{ background: NAVY, color: '#fff' }}>{toast}</div>}
    </div>
  );
};

const FactoryMultiSelect = ({ options, selected, onChange, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggle = (name) => onChange(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full text-left flex items-center justify-between gap-2 transition-all duration-150"
        style={{ background: disabled ? '#F1EEE6' : '#FFFFFF', border: `1px solid ${BORDER}`, color: disabled ? FAINT : selected.length ? NAVY : FAINT, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span className="truncate">{selected.length ? t('mandi.factoriesSelected', '{{count}} Factories selected', { count: selected.length }) : t('common.factory', 'Factory')}</span>
        <Icon name="ChevronDown" size={12} color={disabled ? FAINT : MUTED} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }} />
      </button>

      {open && !disabled && (
        <div className="card-enter absolute z-20 mt-1.5 w-full rounded-xl overflow-hidden max-h-56 overflow-y-auto" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, boxShadow: '0 8px 24px rgba(15,34,56,0.14)' }}>
          {options.length === 0 ? (
            <p className="text-xs px-3 py-3" style={{ color: FAINT }}>{t('mandi.noFactoriesInDistrict', 'No factories in this district yet.')}</p>
          ) : options.map((name) => (
            <label key={name} className="flex items-center gap-2.5 px-3 py-2.5 text-xs cursor-pointer transition-colors duration-100 hover:bg-[#FDF6E9]" style={{ color: NAVY }}>
              <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} style={{ accentColor: AMBER }} />
              <span className="truncate">{name}</span>
            </label>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((name) => (
            <span key={name} className="chip-enter flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: '#FFF3E0', color: AMBER_DEEP, border: '1px solid #FDE1B8' }}>
              {name.length > 22 ? name.slice(0, 22) + '…' : name}
              <button onClick={() => toggle(name)} className="w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: AMBER_DEEP, color: '#fff' }}>
                <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const MandiPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [factories, setFactories] = useState([]);

  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [selectedFactories, setSelectedFactories] = useState([]);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [districtJustUnlocked, setDistrictJustUnlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { factories: rows, error: err } = await fetchMandiListings();
      if (cancelled) return;
      setFactories(rows);
      setError(err);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const states = useMemo(() => [...new Set(factories.map((f) => f.state))].sort(), [factories]);
  const districts = useMemo(
    () => (state ? [...new Set(factories.filter((f) => f.state === state).map((f) => f.district))].sort() : []),
    [factories, state]
  );
  const factoryNames = useMemo(
    () => factories.filter((f) => f.state === state && (!district || f.district === district)).map((f) => f.name).sort(),
    [factories, state, district]
  );

  const handleStateChange = (v) => {
    setState(v);
    setDistrict('');
    setSelectedFactories([]);
    if (v) {
      setDistrictJustUnlocked(true);
      setTimeout(() => setDistrictJustUnlocked(false), 700);
    }
  };
  const handleDistrictChange = (v) => { setDistrict(v); setSelectedFactories([]); };

  const filtered = useMemo(() => factories.filter((f) => {
    if (state && f.state !== state) return false;
    if (district && f.district !== district) return false;
    if (selectedFactories.length > 0 && !selectedFactories.includes(f.name)) return false;
    if (availableOnly && !f.grades.some((g) => g.availableQty > 0)) return false;
    return true;
  }), [factories, state, district, selectedFactories, availableOnly]);

  const hasActiveFilters = state || district || selectedFactories.length > 0 || !availableOnly;

  return (
    <div className="min-h-screen" style={{ background: '#F3EFE4', fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>Mandi — Live Sugar Rates &amp; Buy Sugar Direct from Mills | SugarHathi</title>
        <meta name="description" content="Browse live, mill-approved sugar rates by state, district and factory — and buy sugar direct. Verified by SugarHathi." />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <GlobalStyle />

      <div className="sticky top-0 z-30 bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${AMBER}, #ff6a00)` }}>
              <Icon name="Zap" size={16} color="#fff" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-black" style={{ color: NAVY }}>SugarHathi Mandi</p>
              <p className="text-[10px] truncate" style={{ color: FAINT }}>{t('mandi.tagline', 'Live rates, direct from mills')}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
        {/* Filter bar */}
        <div className="rounded-2xl p-4 mb-5" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}` }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 items-start">
            <select
              value={state}
              onChange={(e) => handleStateChange(e.target.value)}
              className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150"
              style={{ background: '#FFFFFF', border: `1px solid ${state ? AMBER : BORDER}`, color: state ? NAVY : FAINT }}
            >
              <option value="">{t('marketplace.state', 'State')}</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <select
              value={district}
              onChange={(e) => handleDistrictChange(e.target.value)}
              disabled={!state}
              className={`text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150 ${districtJustUnlocked ? 'district-unlock' : ''}`}
              style={{ background: !state ? '#F1EEE6' : '#FFFFFF', border: `1px solid ${district ? AMBER : BORDER}`, color: !state ? FAINT : district ? NAVY : FAINT, opacity: !state ? 0.55 : 1 }}
            >
              <option value="">{state ? t('mandi.selectDistrict', 'District') : t('mandi.selectStateFirst', 'Select a state first')}</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>

            <FactoryMultiSelect options={factoryNames} selected={selectedFactories} onChange={setSelectedFactories} disabled={!state} />

            <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: CREAM, border: `1px solid ${BORDER}` }}>
              <span className="text-xs font-semibold" style={{ color: NAVY }}>{t('mandi.availableQty', 'Available Qty')}</span>
              <button
                onClick={() => setAvailableOnly((v) => !v)}
                role="switch"
                aria-checked={availableOnly}
                className="relative rounded-full flex-shrink-0 transition-colors duration-200"
                style={{ background: availableOnly ? AMBER : '#D6D3CE', width: 38, height: 22 }}
              >
                <span className="absolute top-0.5 rounded-full bg-white shadow transition-transform duration-200" style={{ width: 18, height: 18, left: 2, transform: availableOnly ? 'translateX(16px)' : 'translateX(0)' }} />
              </button>
            </div>
          </div>

          {!state && (
            <p className="text-[11px] mt-2.5 flex items-center gap-1.5" style={{ color: FAINT }}>
              <Icon name="Lock" size={12} color={FAINT} />
              {t('mandi.pickStateToUnlock', 'Pick a state to unlock District and Factory')}
            </p>
          )}

          {hasActiveFilters && (
            <button onClick={() => { setState(''); setDistrict(''); setSelectedFactories([]); setAvailableOnly(true); }} className="text-[11px] font-semibold mt-2.5 hover:underline" style={{ color: AMBER_DEEP }}>
              {t('mandi.resetFilters', 'Reset filters')}
            </button>
          )}
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-8 h-8 rounded-full border-2 animate-spin mb-3" style={{ borderColor: '#fde68a', borderTopColor: AMBER }} />
            <p className="text-sm" style={{ color: MUTED }}>{t('mandi.loadingListings', 'Loading live listings…')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Icon name="AlertTriangle" size={28} color="#DC2626" />
            <p className="text-sm mt-3" style={{ color: MUTED }}>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <p className="text-xs font-semibold mb-3" style={{ color: MUTED }}>{t('mandi.factoriesFound', '{{count}} Factories found', { count: filtered.length })}</p>

            {filtered.length === 0 ? (
              <div className="text-center py-16 rounded-2xl" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}` }}>
                <p className="text-sm" style={{ color: FAINT }}>{factories.length === 0 ? t('mandi.noLiveListings', 'No live listings on the mandi right now — check back soon.') : t('mandi.noFactoriesMatchFilters', 'No factories match these filters.')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start" key={`${state}-${district}-${selectedFactories.join(',')}-${availableOnly}`}>
                {filtered.map((f, i) => <FactoryCard key={f.id} f={f} index={i} />)}
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MandiPage;



